/**
 * WAV writer.
 *
 * Writes 16/24/32-bit PCM and 32-bit float. 24-bit is written as packed 3-byte
 * samples, which is what every DAW expects; 32-bit float is written with
 * WAVE_FORMAT_IEEE_FLOAT so the file round-trips the render bit-for-bit.
 *
 * Files over 4 GB are refused rather than silently truncated: RIFF size fields
 * are 32-bit and an over-long file would be corrupt in a way that is easy to
 * miss until much later.
 */

export type WavFormat = 'pcm16' | 'pcm24' | 'pcm32' | 'float32';

export interface WavOptions {
  sampleRate: number;
  format: WavFormat;
  /** Apply TPDF dither when reducing word length. Ignored for float. */
  dither?: boolean;
}

const BYTES: Record<WavFormat, number> = { pcm16: 2, pcm24: 3, pcm32: 4, float32: 4 };

export function writeWav(channels: Float32Array[], opts: WavOptions): Uint8Array {
  const nCh = channels.length;
  if (nCh < 1) throw new Error('WAV export needs at least one channel.');
  const frames = channels[0].length;
  const bytesPerSample = BYTES[opts.format];
  const bitsPerSample = bytesPerSample * 8;
  const isFloat = opts.format === 'float32';
  const dataBytes = frames * nCh * bytesPerSample;

  if (dataBytes + 44 > 0xffffffff) {
    throw new Error(
      'The rendered file would exceed 4 GB, which a standard WAV header cannot describe. Render a shorter selection or export at a lower bit depth.',
    );
  }

  const buffer = new ArrayBuffer(44 + dataBytes);
  const view = new DataView(buffer);
  const bytes = new Uint8Array(buffer);

  const writeAscii = (offset: number, s: string) => {
    for (let i = 0; i < s.length; i++) bytes[offset + i] = s.charCodeAt(i);
  };

  writeAscii(0, 'RIFF');
  view.setUint32(4, 36 + dataBytes, true);
  writeAscii(8, 'WAVE');
  writeAscii(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, isFloat ? 3 : 1, true);
  view.setUint16(22, nCh, true);
  view.setUint32(24, opts.sampleRate, true);
  view.setUint32(28, opts.sampleRate * nCh * bytesPerSample, true);
  view.setUint16(32, nCh * bytesPerSample, true);
  view.setUint16(34, bitsPerSample, true);
  writeAscii(36, 'data');
  view.setUint32(40, dataBytes, true);

  let offset = 44;
  if (isFloat) {
    for (let i = 0; i < frames; i++) {
      for (let c = 0; c < nCh; c++) {
        view.setFloat32(offset, channels[c][i], true);
        offset += 4;
      }
    }
    return bytes;
  }

  const scale = Math.pow(2, bitsPerSample - 1);
  const maxVal = scale - 1;
  const minVal = -scale;
  const useDither = opts.dither !== false && bitsPerSample <= 24;

  for (let i = 0; i < frames; i++) {
    for (let c = 0; c < nCh; c++) {
      const x = channels[c][i];
      const d = useDither ? (Math.random() + Math.random() - 1) * 0.5 : 0;
      let q = Math.round(x * scale + d);
      if (q > maxVal) q = maxVal;
      if (q < minVal) q = minVal;
      if (bytesPerSample === 2) {
        view.setInt16(offset, q, true);
      } else if (bytesPerSample === 3) {
        bytes[offset] = q & 0xff;
        bytes[offset + 1] = (q >> 8) & 0xff;
        bytes[offset + 2] = (q >> 16) & 0xff;
      } else {
        view.setInt32(offset, q, true);
      }
      offset += bytesPerSample;
    }
  }
  return bytes;
}

/** Read a WAV we wrote, for round-trip verification in the diagnostics panel. */
export function readWav(bytes: Uint8Array): { channels: Float32Array[]; sampleRate: number } | null {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const tag = String.fromCharCode(bytes[0], bytes[1], bytes[2], bytes[3]);
  if (tag !== 'RIFF') return null;
  const fmt = view.getUint16(20, true);
  const nCh = view.getUint16(22, true);
  const sampleRate = view.getUint32(24, true);
  const bits = view.getUint16(34, true);
  const dataSize = view.getUint32(40, true);
  const bytesPerSample = bits / 8;
  const frames = Math.floor(dataSize / (bytesPerSample * nCh));
  const channels: Float32Array[] = [];
  for (let c = 0; c < nCh; c++) channels.push(new Float32Array(frames));
  let offset = 44;
  const scale = Math.pow(2, bits - 1);
  for (let i = 0; i < frames; i++) {
    for (let c = 0; c < nCh; c++) {
      if (fmt === 3) {
        channels[c][i] = view.getFloat32(offset, true);
      } else if (bytesPerSample === 2) {
        channels[c][i] = view.getInt16(offset, true) / scale;
      } else if (bytesPerSample === 3) {
        const b0 = bytes[offset];
        const b1 = bytes[offset + 1];
        const b2 = bytes[offset + 2];
        let v = b0 | (b1 << 8) | (b2 << 16);
        if (v & 0x800000) v |= ~0xffffff;
        channels[c][i] = v / scale;
      } else {
        channels[c][i] = view.getInt32(offset, true) / scale;
      }
      offset += bytesPerSample;
    }
  }
  return { channels, sampleRate };
}

export function downloadBytes(bytes: Uint8Array, fileName: string, mime: string): void {
  const blob = new Blob([bytes as unknown as BlobPart], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke on the next frame: revoking synchronously can cancel the download
  // in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}
